/**
 * ============================================================================
 * L'ÉCOLE SUR CHAQUE APPEL — QUEL QUE SOIT L'APPELANT
 * ============================================================================
 *
 * KLASYS est multi-tenant : le serveur choisit la base de l'école à partir de
 * l'en-tête `X-Tenant`, posé par `services/api.js`. Mais une soixantaine
 * d'appels n'y passent pas — `useSchoolParams`, `useVieScolaire`,
 * `useOrganigrammeParams`, `useEmployeeDetail` et d'autres utilisent `fetch`
 * directement, avec la seule en-tête `Authorization`.
 *
 * Sans `X-Tenant`, le serveur devine à partir de l'hôte. En développement, le
 * proxy remplace l'hôte par `localhost:3001` : la détection retombait alors sur
 * son défaut — `www` — et **la requête allait écrire dans la base d'une autre
 * école**, sans erreur, sans trace. C'est ce qui a fait passer des écritures de
 * csap1 et d'ecole-gestion dans `gestion_scolaire` pendant toute une recette.
 *
 * ----------------------------------------------------------------------------
 * Pourquoi ici, et pas dans les soixante appelants
 * ----------------------------------------------------------------------------
 * Corriger chaque site d'appel, c'est soixante modifications — et la certitude
 * que le soixante-et-unième oubliera. L'école n'est pas une donnée métier que
 * chaque écran choisit : c'est une propriété du transport, au même titre que
 * l'hôte. Elle se pose donc une fois, au niveau du transport.
 *
 * ----------------------------------------------------------------------------
 * Ce que cette enveloppe ne fait pas
 * ----------------------------------------------------------------------------
 * Elle ne touche QUE les appels vers l'API de l'application — chemins relatifs
 * commençant par `/api`, ou URL absolue de même origine. Un `fetch` vers un
 * service tiers ne reçoit rien : envoyer le sous-domaine d'une école à un
 * serveur étranger serait une fuite.
 *
 * Elle n'écrase jamais un `X-Tenant` déjà posé : un appelant qui sait ce qu'il
 * fait — le CIK visant une école précise, par exemple — garde la main.
 */

/** Le sous-domaine ouvert dans le navigateur. `www` pour un `localhost` nu. */
export const tenantDeLHote = () => {
  const hote = window.location.hostname;
  if (hote === 'localhost' || hote === '127.0.0.1') return 'www';

  const parties = hote.split('.');
  // csap1.localhost → csap1   |   csap1.klasys.com → csap1
  if (parties.length >= 2 && parties[parties.length - 1] === 'localhost') return parties[0];
  if (parties.length >= 3) return parties[0];
  return null;
};

const viseNotreApi = (entree) => {
  const url = typeof entree === 'string' ? entree : entree?.url;
  if (typeof url !== 'string') return false;
  if (url.startsWith('/api')) return true;
  try {
    const absolue = new URL(url, window.location.origin);
    return absolue.origin === window.location.origin && absolue.pathname.startsWith('/api');
  } catch {
    return false;
  }
};

export const installerTenantFetch = () => {
  if (typeof window === 'undefined' || window.__tenantFetchInstalle) return;

  const fetchOriginal = window.fetch.bind(window);

  window.fetch = (entree, options = {}) => {
    const tenant = tenantDeLHote();
    if (!tenant || !viseNotreApi(entree)) {
      return fetchOriginal(entree, options);
    }

    // `Headers` accepte indifféremment un objet, un tableau ou un Headers :
    // les appelants utilisent les trois.
    const entetes = new Headers(options.headers || (entree instanceof Request ? entree.headers : undefined));
    if (!entetes.has('X-Tenant')) {
      entetes.set('X-Tenant', tenant);
    }

    return fetchOriginal(entree, { ...options, headers: entetes });
  };

  window.__tenantFetchInstalle = true;
};

export default installerTenantFetch;
